/* Master Tattoo Institute — GSAP animations
   Requires: gsap.min.js + ScrollTrigger.min.js loaded before this file. */
(function () {
  "use strict";
  if (!window.gsap || !window.ScrollTrigger) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  gsap.registerPlugin(ScrollTrigger);
  // Hand reveals over to GSAP (CSS .reveal transitions are disabled via this class)
  document.body.classList.add("gsap-on");

  const isDesktop = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  /* ---------- Split a heading into animatable word spans ---------- */
  function splitWords(el) {
    const words = [];
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(" "));
            } else {
              const wrap = document.createElement("span");
              wrap.className = "w-mask";
              const w = document.createElement("span");
              w.className = "w";
              w.textContent = part;
              wrap.appendChild(w);
              frag.appendChild(wrap);
              words.push(w);
            }
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== "BR") {
          walk(child);
        }
      });
    };
    walk(el);
    return words;
  }

  /* ---------- HERO INTRO (home + inner page heroes) ---------- */
  const hero = document.querySelector(".hero, .page-hero");
  if (hero) {
    const bgImg = hero.querySelector(".hero-bg img");
    const h1 = hero.querySelector("h1");
    const badges = hero.querySelectorAll(".badge");
    const lead = hero.querySelector(".lead");
    const ctas = hero.querySelectorAll(".hero-ctas .btn");
    const crumb = hero.querySelector(".breadcrumb");

    const tl = gsap.timeline({ defaults: { ease: "power4.out" } });

    if (bgImg) {
      gsap.set(bgImg, { animation: "none" }); // take over from the CSS zoom
      tl.fromTo(bgImg, { scale: 1.25, filter: "brightness(0.4)" }, { scale: 1, filter: "brightness(1)", duration: 2.2, ease: "power3.out" }, 0);
    }
    if (crumb) tl.from(crumb, { y: 20, opacity: 0, duration: 0.6 }, 0.25);
    if (badges.length) tl.from(badges, { y: 26, opacity: 0, stagger: 0.09, duration: 0.7 }, 0.35);
    if (h1) {
      const words = splitWords(h1);
      tl.from(words, { yPercent: 118, rotation: 4, stagger: 0.04, duration: 0.9 }, 0.5);
    }
    if (lead) tl.from(lead, { y: 30, opacity: 0, duration: 0.8 }, "-=0.55");
    if (ctas.length) tl.from(ctas, { y: 26, opacity: 0, stagger: 0.12, duration: 0.7 }, "-=0.5");

    // Scroll parallax on the hero background
    if (bgImg) {
      gsap.to(bgImg, {
        yPercent: 16,
        ease: "none",
        scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: true },
      });
    }
  }

  /* ---------- Parallax on every other full-bleed band (Miami, etc.) ---------- */
  document.querySelectorAll("section.page-hero").forEach((band, i) => {
    if (i === 0 && band === hero) return; // hero already handled
    const img = band.querySelector(".hero-bg img");
    if (!img) return;
    gsap.fromTo(
      img,
      { yPercent: -12 },
      {
        yPercent: 12,
        ease: "none",
        scrollTrigger: { trigger: band, start: "top bottom", end: "bottom top", scrub: true },
      }
    );
  });

  /* ---------- Generic scroll reveals (replaces CSS .reveal) ----------
     Elements with their own specialized animation (cards, pillars, CTA band,
     galleries, spotlight portraits) are excluded to avoid double-tweening. */
  const genericReveals = gsap.utils
    .toArray(".reveal")
    .filter((el) => !el.matches(".program-card, .pillar, .cta-band, .spotlight-media, .gallery-grid"));
  ScrollTrigger.batch(genericReveals, {
    start: "top 88%",
    once: true,
    onEnter: (batch) =>
      gsap.from(batch, {
        y: 48,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        stagger: 0.14,
        overwrite: "auto",
        clearProps: "transform,opacity",
      }),
  });

  /* ---------- Program / faculty cards: rise with slight rotation ---------- */
  ScrollTrigger.batch(".program-card", {
    start: "top 90%",
    once: true,
    onEnter: (batch) =>
      gsap.from(batch, {
        y: 70,
        opacity: 0,
        rotationX: 8,
        transformPerspective: 800,
        transformOrigin: "center bottom",
        duration: 1,
        ease: "power3.out",
        stagger: 0.12,
        clearProps: "all",
      }),
  });

  /* ---------- Gallery: scale-in stagger ---------- */
  ScrollTrigger.batch(".gallery-item", {
    start: "top 92%",
    once: true,
    onEnter: (batch) =>
      gsap.from(batch, {
        scale: 0.85,
        y: 40,
        opacity: 0,
        duration: 0.9,
        ease: "power3.out",
        stagger: { each: 0.08, from: "start" },
        clearProps: "all",
      }),
  });

  /* ---------- Spotlight portraits: cinematic clip reveal ----------
     The tween is built inside onEnter, never at page load. That matters: this
     animation hides content (it clips the frame in from the right), so if it
     were created up front with immediateRender, any trigger that failed to
     fire or complete would leave the portrait permanently cut off. Built here,
     the worst case is that the wipe never plays and the portrait is simply
     visible. clearProps on both completion and interruption guarantees the
     resting state is plain CSS with no inline clip-path left behind. */
  ScrollTrigger.batch(".photo-frame", {
    start: "top 82%",
    once: true,
    onEnter: (batch) =>
      batch.forEach((frame) => {
        const img = frame.querySelector("img");
        const settle = () => gsap.set(frame, { clearProps: "clipPath" });
        const tl = gsap.timeline({ onComplete: settle, onInterrupt: settle });
        tl.fromTo(
          frame,
          { clipPath: "inset(0 100% 0 0)" },
          { clipPath: "inset(0 0% 0 0)", duration: 1.15, ease: "power4.inOut" }
        );
        if (img) {
          tl.from(img, { scale: 1.35, duration: 1.15, ease: "power3.out", clearProps: "transform" }, 0.05);
        }
      }),
  });

  /* ---------- Timeline items: draw in from the line ---------- */
  document.querySelectorAll(".tl-item").forEach((item) => {
    gsap.from(item, {
      x: -46,
      opacity: 0,
      duration: 0.85,
      ease: "power3.out",
      scrollTrigger: { trigger: item, start: "top 88%", once: true },
    });
  });

  /* ---------- Pillars: pop with back-ease ---------- */
  ScrollTrigger.batch(".pillar", {
    start: "top 90%",
    once: true,
    onEnter: (batch) =>
      gsap.from(batch, {
        y: 54,
        opacity: 0,
        scale: 0.94,
        duration: 0.85,
        ease: "back.out(1.4)",
        stagger: 0.1,
        clearProps: "all",
      }),
  });

  /* ---------- Marquee: skew reacts to scroll velocity ---------- */
  const marqueeTrack = document.querySelector(".marquee-track");
  if (marqueeTrack) {
    const skewTo = gsap.quickTo(marqueeTrack, "skewX", { duration: 0.5, ease: "power2.out" });
    ScrollTrigger.create({
      onUpdate: (self) => skewTo(gsap.utils.clamp(-8, 8, self.getVelocity() / 300)),
    });
  }

  /* ---------- CTA band: glow sweep ---------- */
  document.querySelectorAll(".cta-band").forEach((band) => {
    gsap.from(band, {
      scale: 0.94,
      opacity: 0,
      duration: 1,
      ease: "power3.out",
      scrollTrigger: { trigger: band, start: "top 85%", once: true },
    });
  });

  if (isDesktop) {
    /* ---------- Magnetic gold buttons ---------- */
    document.querySelectorAll(".btn-gold").forEach((btn) => {
      const xTo = gsap.quickTo(btn, "x", { duration: 0.4, ease: "power3.out" });
      const yTo = gsap.quickTo(btn, "y", { duration: 0.4, ease: "power3.out" });
      btn.addEventListener("mousemove", (e) => {
        const r = btn.getBoundingClientRect();
        xTo((e.clientX - r.left - r.width / 2) * 0.28);
        yTo((e.clientY - r.top - r.height / 2) * 0.38);
      });
      btn.addEventListener("mouseleave", () => {
        gsap.to(btn, { x: 0, y: 0, duration: 0.8, ease: "elastic.out(1, 0.35)" });
      });
    });

    /* ---------- 3D tilt on program cards ---------- */
    document.querySelectorAll(".program-card").forEach((card) => {
      const rx = gsap.quickTo(card, "rotationX", { duration: 0.5, ease: "power2.out" });
      const ry = gsap.quickTo(card, "rotationY", { duration: 0.5, ease: "power2.out" });
      // set on hover (not load) so the reveal batch's clearProps can't wipe it
      card.addEventListener("mouseenter", () => gsap.set(card, { transformPerspective: 900 }));
      card.addEventListener("mousemove", (e) => {
        const r = card.getBoundingClientRect();
        ry(((e.clientX - r.left) / r.width - 0.5) * 8);
        rx(-((e.clientY - r.top) / r.height - 0.5) * 8);
      });
      card.addEventListener("mouseleave", () => {
        gsap.to(card, { rotationX: 0, rotationY: 0, duration: 0.7, ease: "power3.out" });
      });
    });

    /* ---------- Cursor ink dot ---------- */
    const dot = document.createElement("div");
    dot.className = "cursor-dot";
    document.body.appendChild(dot);
    const dotX = gsap.quickTo(dot, "x", { duration: 0.25, ease: "power3.out" });
    const dotY = gsap.quickTo(dot, "y", { duration: 0.25, ease: "power3.out" });
    window.addEventListener("mousemove", (e) => {
      dotX(e.clientX);
      dotY(e.clientY);
    });
    document.querySelectorAll("a, button, .gallery-item, .video-frame").forEach((el) => {
      el.addEventListener("mouseenter", () => dot.classList.add("grow"));
      el.addEventListener("mouseleave", () => dot.classList.remove("grow"));
    });
  }

  /* ---------- Refresh after images load (layout shifts) ----------
     window.load does NOT wait for loading="lazy" images, so on Shopify — where
     every section image is lazy and served from the CDN — trigger positions
     were being measured against a page that had not finished growing. Stale
     positions mean a reveal can fail to fire and leave its element stuck in
     the tween's "from" state. Re-measure as each lazy image actually lands. */
  window.addEventListener("load", () => ScrollTrigger.refresh());

  let refreshTimer;
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 120);
  }
  document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    if (img.complete) return;
    img.addEventListener("load", scheduleRefresh, { once: true });
    img.addEventListener("error", scheduleRefresh, { once: true });
  });
})();
